import { Check } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fi } from 'date-fns/locale';
import { useTranslate } from '../lib/i18n';
import analyticsService from '../services/analyticsService';
import { Calendar } from './ui/calendar';
import Button from './common/Button';

export interface PasswordCellRef {
  focus: () => void;
}

interface PasswordCellProps {
  onEnter?: () => void;
  index: number;
  onFocus?: (index: number) => void;
  onBlurCell?: (index: number) => void;
  value: string;
  onChange: (index: number, value: string) => void;
}

const PasswordCell = forwardRef<PasswordCellRef, PasswordCellProps>(
  ({ onEnter, index, onFocus, onBlurCell, value, onChange }, ref) => {
    const [state, setState] = useState<'empty' | 'writing' | 'complete'>(
      value.trim().length > 0 ? 'complete' : 'empty'
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        setState('writing');
        if (onFocus) {
          onFocus(index);
        }
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      },
    }));

    const handleClick = () => {
      if (state === 'empty' || state === 'complete') {
        setState('writing');
        if (onFocus) {
          onFocus(index);
        }
      }
    };

    useEffect(() => {
      if (state === 'writing' && inputRef.current) {
        inputRef.current.focus();
      }
    }, [state]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if ((e.key === 'Enter' || e.key === 'Tab' || e.key === '-') && onEnter) {
        e.preventDefault();
        onEnter();
      }
    };

    const onBlur = () => {
      if (value.trim().length === 0) {
        setState('empty');
      } else {
        setState('complete');
      }
      if (onBlurCell) {
        onBlurCell(index);
      }
    };

    const getClassName = () => {
      const baseClasses =
        'border rounded px-2 flex items-center justify-center text-center h-10 text-4xl';

      if (state === 'empty') {
        return `${baseClasses} flex-1 min-w-[40px] border-border`;
      }
      if (state === 'writing') {
        return `${baseClasses} flex-[999] min-w-0 border-foreground bg-card`;
      }
      return `${baseClasses} flex-1 min-w-[40px] border-foreground bg-success/10`;
    };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        className={getClassName()}
      >
        {state === 'complete' && <Check className="h-[20px] w-[20px] flex-shrink-0" />}
        {state === 'writing' && (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(index, e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            className="focus:outline-none w-full text-lg bg-transparent"
          />
        )}
        {state === 'empty' && <span className="mt-4 select-none">*</span>}
      </div>
    );
  }
);

interface PasswordInputProps {
  onSubmit?: (data: { selectedDate: Date; passwordValues: string[] }) => void;
}

const PasswordInput = ({ onSubmit }: PasswordInputProps) => {
  const cellRefs = useRef<(PasswordCellRef | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState<'date' | 'password'>('date');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [passwordValues, setPasswordValues] = useState<string[]>(['', '', '']);
  const t = useTranslate();

  const handleEnter = (index: number) => {
    const nextIndex = index + 1;
    if (nextIndex < cellRefs.current.length && cellRefs.current[nextIndex]) {
      cellRefs.current[nextIndex]?.focus();
    } else {
      // We're on the last cell, submit the password
      if (isSubmitEnabled) {
        handleSubmit();
      }
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
  };

  const handleBlur = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : prev));
  };

  const handlePasswordChange = (index: number, value: string) => {
    const newValues = [...passwordValues];
    newValues[index] = value;
    setPasswordValues(newValues);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setCurrentStep('password');
    }
  };

  const getMessageForIndex = () => {
    if (activeIndex === null) return '';
    const messages = [
      t('SurveyTool.passwordWelcome.firstNote'),
      t('SurveyTool.passwordWelcome.secondNote'),
      t('SurveyTool.passwordWelcome.thirdNote'),
    ];
    return messages[activeIndex] || '';
  };

  const isSubmitEnabled = selectedDate && passwordValues.every((value) => value.trim().length > 0);

  const handleSubmit = () => {
    if (isSubmitEnabled && selectedDate) {
      const submissionData = { selectedDate, passwordValues };
      if (onSubmit) {
        analyticsService.trackEvent({ eventName: 'returnCodeSubmitted' });
        onSubmit(submissionData);
      }
    }
  };

  if (currentStep === 'date') {
    return (
      <div className="max-w-lg mx-auto min-h-full flex flex-col justify-center">
        <div>
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">
              {t('SurveyTool.passwordInput.selectDate')}
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              {t('SurveyTool.passwordInput.selectDateDetails')}
            </p>
          </div>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            className="mx-auto"
            locale={fi}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto min-h-full flex flex-col justify-between">
      <div className="flex-1 flex items-center">
        <div className="w-full space-y-4">
          <p className="text-muted-foreground text-sm text-center">
            {t('SurveyTool.passwordInput.enterPassword')}
          </p>
          <div className="flex items-center gap-2 w-full">
            <PasswordCell
              ref={(el) => {
                cellRefs.current[0] = el;
              }}
              index={0}
              value={passwordValues[0] ?? ''}
              onChange={handlePasswordChange}
              onEnter={() => handleEnter(0)}
              onFocus={handleFocus}
              onBlurCell={() => handleBlur(0)}
            />
            <span>-</span>
            <PasswordCell
              ref={(el) => {
                cellRefs.current[1] = el;
              }}
              index={1}
              value={passwordValues[1] ?? ''}
              onChange={handlePasswordChange}
              onEnter={() => handleEnter(1)}
              onFocus={handleFocus}
              onBlurCell={() => handleBlur(1)}
            />
            <span>-</span>
            <PasswordCell
              ref={(el) => {
                cellRefs.current[2] = el;
              }}
              index={2}
              value={passwordValues[2] ?? ''}
              onChange={handlePasswordChange}
              onEnter={() => handleEnter(2)}
              onFocus={handleFocus}
              onBlurCell={() => handleBlur(2)}
            />
          </div>

          {/* Prevent content jumping around because info-text is not always displayed. */}
          <p
            className={`text-muted-foreground text-sm text-center ${!getMessageForIndex() ? 'mb-9' : ''}`}
          >
            {getMessageForIndex()}
          </p>
        </div>
      </div>

      <div className="pb-8">
        <Button onClick={handleSubmit} disabled={!isSubmitEnabled} className="w-full">
          {t('SurveyTool.passwordInput.submitPassword')}
        </Button>
      </div>
    </div>
  );
};

export default PasswordInput;
